// Hook customizado para gerenciar estado e lógica do Dashboard
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { dashboardService } from "../services/dashboardService";
import { storageService } from "../services/storageService";
import {
  populateCounters,
  calculateInfestations,
  extractMunicipalities,
  filterPlotsByMunicipality
} from "../utils/helpers";
import { PLAGUE_NAMES } from "../utils/constants";
import { MESSAGES } from "../utils/constants";
import logger from "../../../utils/logger";
import { isMockMode } from "../../../utils/mockData";

/**
 * Hook para gerenciar estado e lógica do Dashboard
 * @returns {Object} Estado e funções do Dashboard
 */
export const useDashboard = () => {
  const navigate = useNavigate();
  
  // Estados principais
  const [occurrences, setOccurrences] = useState([]);
  const [occurrencesSort, setOccurrencesSort] = useState([]);
  const [loading, setLoading] = useState(true);
  const [camouflage, setCamouflage] = useState(true);
  
  // Estados de filtros e visualização
  const [showCards, setShowCards] = useState(false);
  const [enablePlague, setEnablePlague] = useState(false);
  const [plagueSelected, setPlagueSelected] = useState("");
  const [plots, setPlots] = useState([]);
  
  // Estados de contadores
  const [hectar, setHectar] = useState(0);
  const [alto, setAlto] = useState(0);
  const [medio, setMedio] = useState(0);
  const [baixo, setBaixo] = useState(0);
  
  // Estados de dados processados
  const [plagues, setPlagues] = useState([]);
  const [municipality, setMunicipality] = useState([]);
  
  // Refs para controle de execução única
  const aux2Ref = useRef(0);

  /**
   * Carrega dados de previsão
   */
  const loadPrediction = useCallback(async () => {
    setLoading(true);
    
    try {
      let data = [];
      
      if (navigator.onLine) {
        data = await dashboardService.getPrediction();
      } else {
        // Modo offline - usar dados do localStorage
        logger.info("Modo offline detectado");
        const uniqueOccurrences = storageService.getUniqueOccurrences();
        
        if (uniqueOccurrences && uniqueOccurrences.length > 0) {
          // Simular delay de carregamento
          await new Promise(resolve => setTimeout(resolve, 3000));
          data = uniqueOccurrences;
        } else {
          logger.warn("Nenhum dado offline disponível");
          data = [];
        }
      }

      if (data.length === 0) {
        logger.warn("Nenhum dado disponível");
        setOccurrences([]);
        setCamouflage(false);
        setLoading(false);
        return;
      }

      // Processar dados
      setOccurrences(data);
      
      // Calcular contadores
      const counters = populateCounters(data);
      setAlto(counters.high);
      setMedio(counters.medium);
      setBaixo(counters.low);
      setHectar(counters.hectar);
      setOccurrencesSort(counters.sortedOccurrences);
      setPlots(counters.sortedOccurrences);
      
      // Salvar ocorrências ordenadas no localStorage
      storageService.setOccurrencesSort(counters.sortedOccurrences);

      // Calcular infestações por praga
      const infestations = calculateInfestations(data, PLAGUE_NAMES);
      setPlagues(infestations);

      // Extrair municípios
      const municipalities = extractMunicipalities(data);
      setMunicipality(municipalities);

      // Desativar camuflagem
      setCamouflage(false);
      
      // Mostrar cards automaticamente quando houver dados
      if (counters.sortedOccurrences && counters.sortedOccurrences.length > 0) {
        setShowCards(true);
      }
    } catch (error) {
      logger.error("Erro ao carregar previsões", error);
      
      // Só redireciona para login se for erro de autenticação (401/403)
      // Erros de rede não devem redirecionar
      if (error.message === "Not authenticated" && error.response?.status === 401) {
        navigate("/");
        return;
      }
      
      toast.error(MESSAGES.LOADING_ERROR);
      setCamouflage(false);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  /**
   * Envia registros offline quando volta online
   */
  const sendOfflineRegisters = useCallback(async () => {
    if (aux2Ref.current >= 1) return;
    
    const registers = storageService.getRegistersToInvite();
    if (!registers) return;

    aux2Ref.current += 1;

    // Verificar se está em modo mock
    if (isMockMode()) {
      logger.info("Modo mock: interceptando envio de registros offline");
      try {
        await dashboardService.sendOfflineRegisters(registers);
        toast.success(MESSAGES.REGISTERS_SUCCESS);
        storageService.removeRegistersToInvite();
      } catch (error) {
        logger.error("Erro ao processar registros mock offline", error);
        toast.error(MESSAGES.REGISTERS_ERROR);
      }
      return;
    }

    try {
      await dashboardService.sendOfflineRegisters(registers);
      toast.success(MESSAGES.REGISTERS_SUCCESS);
      storageService.removeRegistersToInvite();
    } catch (error) {
      logger.error("Erro ao enviar registros offline", error);
      toast.error(MESSAGES.REGISTERS_ERROR);
    }
  }, []);

  /**
   * Handler para visualizar cards
   */
  const handleViewCards = useCallback(() => {
    setShowCards(true);
  }, []);

  /**
   * Handler para mudança de município
   */
  const handleChangeMunicipio = useCallback((value) => {
    setShowCards(false);
    const municipality = value.target.value;
    const filteredPlots = filterPlotsByMunicipality(occurrencesSort, municipality);
    setPlots(filteredPlots);
    setEnablePlague(true);
  }, [occurrencesSort]);

  /**
   * Handler para mudança de praga
   */
  const handleChangePlague = useCallback((value) => {
    setPlagueSelected(value.target.value);
    setShowCards(false);
  }, []);

  // Carregar dados ao montar componente
  useEffect(() => {
    loadPrediction();
  }, [loadPrediction]);

  // Enviar registros offline quando voltar online
  useEffect(() => {
    if (navigator.onLine && aux2Ref.current === 0) {
      sendOfflineRegisters();
    }
  }, [sendOfflineRegisters]);

  return {
    // Estados
    occurrences,
    occurrencesSort,
    loading,
    camouflage,
    showCards,
    enablePlague,
    plagueSelected,
    plots,
    hectar,
    alto,
    medio,
    baixo,
    plagues,
    municipality,
    
    // Handlers
    handleViewCards,
    handleChangeMunicipio,
    handleChangePlague,
    setShowCards
  };
};

